import React, { useState, useRef, useEffect } from 'react';
import {
  MapPin,
  ArrowUpDown,
  Plus,
  X,
  Clock,
  Navigation,
  Menu,
  ChevronDown,
  ChevronUp,
  ShieldOff,
  Check,
} from 'lucide-react';
import type { TripEndpointRow, PlaceSuggestion, AvoidOptions } from '../types';
import { searchPlaces, reverseGeocode } from '../services/places';
import { formatDuration, formatClockTime } from '../utils/units';

type SearchCardProps = {
  endpoints: TripEndpointRow[];
  onUpdateEndpoints: (endpoints: TripEndpointRow[]) => void;
  onOpenDwellPicker: (index: number) => void;
  onBuildRoute: (customEndpoints?: TripEndpointRow[]) => void;
  isBuilding: boolean;
  weatherLoadingProgress: number | null; // 0 to 1, or null if idle
  canAddStop: boolean;
  onAddStop: () => void;
  onRemoveStop: (index: number) => void;
  onToast?: (msg: string, type?: 'info' | 'success' | 'warning') => void;
  avoidOptions: AvoidOptions;
  onChangeAvoidOptions: (options: AvoidOptions) => void;
  departureTime?: Date;
  onOpenDepartureModal?: () => void;
};

export const SearchCard: React.FC<SearchCardProps> = ({
  endpoints,
  onUpdateEndpoints,
  onOpenDwellPicker,
  onBuildRoute,
  isBuilding,
  weatherLoadingProgress,
  canAddStop,
  onAddStop,
  onRemoveStop,
  onToast,
  avoidOptions,
  onChangeAvoidOptions,
  departureTime,
  onOpenDepartureModal,
}) => {
  const [activeRowIdx, setActiveRowIdx] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [showAvoidMenu, setShowAvoidMenu] = useState(false);
  const debounceTimerRef = useRef<number | null>(null);
  const searchGenRef = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const dismissSuggestions = () => {
    searchGenRef.current += 1;
    setSuggestions([]);
    setActiveRowIdx(null);
    setIsSearching(false);
  };

  // Dismiss suggestions & avoid menu on outside tap/click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        dismissSuggestions();
        setShowAvoidMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const startRow = endpoints[0]!;
  const destRow = endpoints[endpoints.length - 1]!;
  const vias = endpoints.slice(1, endpoints.length - 1);

  // Debounced search on query change
  const handleQueryChange = (idx: number, query: string) => {
    const updated = [...endpoints];
    const prev = updated[idx]!;
    updated[idx] = { ...prev, query, place: null };
    onUpdateEndpoints(updated);
    setActiveRowIdx(idx);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (query.trim().length < 2) {
      searchGenRef.current += 1;
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    const gen = ++searchGenRef.current;
    setIsSearching(true);
    debounceTimerRef.current = window.setTimeout(async () => {
      const results = await searchPlaces(query);
      if (gen !== searchGenRef.current) return;
      setSuggestions(results);
      setIsSearching(false);
    }, 350);
  };

  const handleSelectSuggestion = (suggestion: PlaceSuggestion) => {
    if (activeRowIdx == null) return;
    const updated = [...endpoints];
    const prev = updated[activeRowIdx]!;
    updated[activeRowIdx] = {
      ...prev,
      query: suggestion.shortName,
      place: suggestion,
    };
    onUpdateEndpoints(updated);
    dismissSuggestions();
    (document.activeElement as HTMLElement)?.blur();

    // If both endpoints now have valid places, auto-trigger build
    const valid = updated.filter((e) => e.place !== null);
    if (valid.length >= 2 && updated.every((e) => e.place !== null)) {
      onBuildRoute(updated);
    }
  };

  // Auto-resolve any rows that have text but place === null, then build route
  const handleAutoResolveAndBuild = async (currentRows: TripEndpointRow[]) => {
    dismissSuggestions();
    setShowAvoidMenu(false);
    (document.activeElement as HTMLElement)?.blur();
    let resolved = [...currentRows];
    const missing = resolved
      .map((r, i) => (r.query.trim().length > 0 && !r.place ? i : -1))
      .filter((i) => i !== -1);

    if (missing.length > 0) {
      setIsSearching(true);
      try {
        await Promise.all(
          missing.map(async (rowIdx) => {
            const row = resolved[rowIdx]!;
            const results = await searchPlaces(row.query.trim());
            if (results.length > 0) {
              resolved[rowIdx] = {
                ...row,
                query: results[0]!.shortName,
                place: results[0]!,
              };
            }
          }),
        );
        onUpdateEndpoints(resolved);
      } finally {
        setIsSearching(false);
      }
    }

    const filled = resolved.filter((r) => r.place !== null);
    if (filled.length >= 2) {
      dismissSuggestions();
      onBuildRoute(resolved);
    } else if (onToast) {
      onToast('Please enter both start and destination locations.', 'warning');
    }
  };

  // Handle Enter key on input fields
  const handleKeyDown = async (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // If dropdown has suggestions for this row, pick top suggestion immediately
      if (suggestions.length > 0 && activeRowIdx === idx) {
        const top = suggestions[0]!;
        const updated = [...endpoints];
        updated[idx] = {
          ...updated[idx]!,
          query: top.shortName,
          place: top,
        };
        onUpdateEndpoints(updated);
        dismissSuggestions();
        await handleAutoResolveAndBuild(updated);
        return;
      }

      // If text is entered but not resolved yet
      const row = endpoints[idx]!;
      if (row.query.trim().length >= 2) {
        setIsSearching(true);
        try {
          const results = await searchPlaces(row.query.trim());
          if (results.length > 0) {
            const top = results[0]!;
            const updated = [...endpoints];
            updated[idx] = {
              ...updated[idx]!,
              query: top.shortName,
              place: top,
            };
            onUpdateEndpoints(updated);
            setSuggestions([]);
            setActiveRowIdx(null);
            await handleAutoResolveAndBuild(updated);
            return;
          }
        } finally {
          setIsSearching(false);
        }
      }

      await handleAutoResolveAndBuild(endpoints);
    }
  };

  // Use current location for start row
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      if (onToast) {
        onToast('Geolocation is not supported by your browser.', 'warning');
      }
      return;
    }

    setIsSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coord = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
        const name = await reverseGeocode(coord);
        const suggestion: PlaceSuggestion = {
          id: `gps-${Date.now()}`,
          displayName: name || 'Current Location',
          shortName: name ? name.split(',')[0]! : 'Current Location',
          coordinate: coord,
        };

        const updated = [...endpoints];
        updated[0] = {
          ...updated[0]!,
          query: suggestion.shortName,
          place: suggestion,
        };
        onUpdateEndpoints(updated);
        setIsSearching(false);

        if (updated[1]?.place) {
          onBuildRoute(updated);
        }
      },
      (err) => {
        setIsSearching(false);
        const msg = err.code === 1 ? 'Location access was denied.' : `Location unavailable: ${err.message}`;
        if (onToast) {
          onToast(msg, 'warning');
        }
      },
      { timeout: 10000, enableHighAccuracy: true },
    );
  };

  // Swap start and destination (2-point trips)
  const handleSwap = () => {
    if (endpoints.length !== 2) return;
    const updated = [
      { ...endpoints[1]!, dwellSec: 0 },
      { ...endpoints[0]!, dwellSec: 0 },
    ];
    onUpdateEndpoints(updated);
    if (updated[0]?.place && updated[1]?.place) {
      onBuildRoute(updated);
    }
  };

  // Move via stop up / down
  const handleMoveVia = (viaIndex: number, direction: 'up' | 'down') => {
    const targetViaIndex = direction === 'up' ? viaIndex - 1 : viaIndex + 1;
    if (targetViaIndex < 0 || targetViaIndex >= vias.length) return;

    const newVias = [...vias];
    const temp = newVias[viaIndex]!;
    newVias[viaIndex] = newVias[targetViaIndex]!;
    newVias[targetViaIndex] = temp;

    onUpdateEndpoints([startRow, ...newVias, destRow]);
  };

  const isMultiStop = endpoints.length >= 3;
  const hasTextOrPlace = endpoints.filter((e) => e.place !== null || e.query.trim().length >= 2);
  const canBuild = hasTextOrPlace.length >= 2;

  const avoidActiveCount = (avoidOptions.avoidTolls ? 1 : 0) + (avoidOptions.avoidHighways ? 1 : 0);
  const avoidLabel =
    avoidActiveCount === 2
      ? 'Avoid (2)'
      : avoidOptions.avoidTolls
      ? 'Avoid: Tolls'
      : avoidOptions.avoidHighways
      ? 'Avoid: Highways'
      : 'Avoid';

  return (
    <div
      ref={cardRef}
      className="card-surface glass"
      style={{
        padding: '12px 14px',
        position: 'relative',
        borderRadius: 24,
        zIndex: activeRowIdx !== null ? 100 : 1,
      }}
    >
      {/* Endpoints Rows */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {endpoints.map((row, idx) => {
          const isStart = idx === 0;
          const isDest = idx === endpoints.length - 1;
          const isVia = !isStart && !isDest;
          const letter = String.fromCharCode(65 + idx);

          return (
            <React.Fragment key={row.id}>
              {/* Row item */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 40,
                  gap: 10,
                  position: 'relative',
                }}
              >
                {/* Row Icon / Letter badge */}
                <div style={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                  {isStart ? (
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        border: '2.5px solid var(--accent)',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : isDest ? (
                    <MapPin size={17} color="#FF3B30" />
                  ) : (
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 9,
                        backgroundColor: 'var(--accent)',
                        color: '#FFF',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {letter}
                    </div>
                  )}
                </div>

                {/* Input field */}
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    value={row.query}
                    placeholder={
                      isStart
                        ? 'Start address or current location'
                        : isDest
                        ? 'Destination'
                        : 'Stop'
                    }
                    onChange={(e) => handleQueryChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    onFocus={() => {
                      setActiveRowIdx(idx);
                      // Already-resolved place: keep the route card visible; search again only after the query changes.
                      if (row.place && row.query.trim() === row.place.shortName) {
                        searchGenRef.current += 1;
                        setSuggestions([]);
                        setIsSearching(false);
                        return;
                      }
                      if (row.query.trim().length >= 2) {
                        const gen = ++searchGenRef.current;
                        setIsSearching(true);
                        searchPlaces(row.query).then((results) => {
                          if (gen !== searchGenRef.current) return;
                          setSuggestions(results);
                          setIsSearching(false);
                        });
                      }
                    }}
                    className="search-field"
                    style={{
                      width: '100%',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--label)',
                      fontSize: 16,
                      fontWeight: 500,
                      padding: '4px 0',
                      boxSizing: 'border-box',
                    }}
                    aria-label={isStart ? 'Start location' : isDest ? 'Destination location' : `Stop ${letter}`}
                  />
                </div>

                {/* Trailing actions: Location button, Dwell picker, Remove, or Swap */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isStart && !row.place && (
                    <button
                      className="cbtn"
                      onClick={handleUseCurrentLocation}
                      title="Use current location"
                      aria-label="Use current location"
                    >
                      <Navigation size={14} color="var(--accent)" />
                    </button>
                  )}

                  {isStart && endpoints.length === 2 && (
                    <button
                      className="cbtn"
                      onClick={handleSwap}
                      title="Swap start and destination"
                      aria-label="Swap route"
                    >
                      <ArrowUpDown size={14} />
                    </button>
                  )}

                  {isVia && (
                    <>
                      <button
                        onClick={() => onOpenDwellPicker(idx)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          height: 24,
                          padding: '0 8px',
                          borderRadius: 12,
                          border: 'none',
                          background: 'var(--fill2)',
                          color: 'var(--label2)',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <Clock size={11} />
                        <span>{row.dwellSec ? formatDuration(row.dwellSec) : '0 min'}</span>
                      </button>

                      <button
                        className="cbtn"
                        style={{ width: 24, height: 24 }}
                        onClick={() => onRemoveStop(idx)}
                        aria-label="Remove stop"
                      >
                        <X size={12} />
                      </button>
                    </>
                  )}

                  {isDest && isMultiStop && (
                    <button
                      className="cbtn"
                      onClick={() => setShowReorderModal(true)}
                      title="Reorder stops"
                      aria-label="Reorder stops"
                    >
                      <Menu size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Divider between rows with optional "+ Add Stop" divider button */}
              {idx < endpoints.length - 1 && (
                <div
                  style={{
                    position: 'relative',
                    height: 1,
                    backgroundColor: 'var(--sep)',
                    margin: '2px 0 2px 30px',
                    marginRight: idx === endpoints.length - 2 && canAddStop ? 96 : 0,
                  }}
                >
                  {/* On divider above destination, provide Add Stop shortcut terminating cleanly */}
                  {idx === endpoints.length - 2 && canAddStop && (
                    <button
                      type="button"
                      onClick={onAddStop}
                      style={{
                        position: 'absolute',
                        left: '100%',
                        marginLeft: 8,
                        top: -11,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        height: 22,
                        padding: '0 8px',
                        borderRadius: 11,
                        border: '1px solid var(--sep)',
                        background: 'var(--chrome)',
                        color: 'var(--accent)',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Plus size={12} />
                      <span>Add Stop</span>
                    </button>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Autocomplete Suggestions Dropdown */}
      {activeRowIdx !== null && (suggestions.length > 0 || isSearching) && (
        <div
          className="place-suggestions"
          data-testid="place-suggestions"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 8,
            borderRadius: 18,
            boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
            maxHeight: 220,
            overflowY: 'auto',
            zIndex: 200,
            padding: '6px 0',
          }}
        >
          {isSearching && (
            <div className="t-foot c2" style={{ padding: '8px 16px' }}>
              Searching places...
            </div>
          )}

          {suggestions.map((sug) => (
            <div
              key={sug.id}
              className="cell"
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
              }}
              onClick={() => handleSelectSuggestion(sug)}
            >
              <MapPin size={15} color="var(--accent)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-body" style={{ fontWeight: 600 }}>
                  {sug.shortName}
                </div>
                {sug.subName && (
                  <div className="t-foot c2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sug.subName}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar: Departure Scrubber Button + Avoid Menu + Build Route */}
      <div
        style={{
          marginTop: 10,
          paddingTop: 8,
          borderTop: '0.5px solid var(--sep)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          position: 'relative',
          flexWrap: 'nowrap',
          minWidth: 0,
        }}
      >
        {/* Departure Time Button */}
        {onOpenDepartureModal && (
          <button
            type="button"
            className="cbtn"
            style={{
              height: 32,
              maxHeight: 32,
              borderRadius: 16,
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11.5,
              fontWeight: 600,
              color: 'var(--label)',
              border: '1px solid var(--sep)',
              background: 'var(--card)',
              whiteSpace: 'nowrap',
              flex: '1 1 auto',
              flexShrink: 1,
              minWidth: 0,
              maxWidth: 168,
              overflow: 'hidden',
            }}
            onClick={() => {
              dismissSuggestions();
              onOpenDepartureModal();
            }}
            title="Change departure time"
            aria-label={`Departure time, Leave ${departureTime ? formatClockTime(departureTime) : 'Now'}`}
          >
            <Clock size={12} color="var(--accent)" style={{ flexShrink: 0 }} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              Leave {departureTime ? formatClockTime(departureTime) : 'Now'}
            </span>
            <ChevronDown size={11} color="var(--label3)" style={{ flexShrink: 0 }} />
          </button>
        )}

        {/* Avoid Menu Toggle Button */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="cbtn"
            style={{
              height: 32,
              borderRadius: 16,
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 12,
              fontWeight: 600,
              color: avoidActiveCount > 0 ? 'var(--accent)' : 'var(--label)',
              border: avoidActiveCount > 0 ? '1.5px solid var(--accent)' : '1px solid var(--sep)',
              background: avoidActiveCount > 0 ? 'var(--fill2)' : 'var(--card)',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              dismissSuggestions();
              setShowAvoidMenu(!showAvoidMenu);
            }}
            title="Route avoidance options"
            aria-label="Avoid options"
          >
            <ShieldOff size={12} />
            <span>{avoidLabel}</span>
            <ChevronDown size={11} color="var(--label3)" />
          </button>

          {/* Avoid Menu Popover */}
          {showAvoidMenu && (
            <div
              className="sheet-surface"
              style={{
                position: 'absolute',
                top: 38,
                left: 0,
                width: 220,
                borderRadius: 18,
                boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
                zIndex: 1050,
                padding: '8px 0',
              }}
            >
              <div
                className="t-foot c2"
                style={{
                  padding: '4px 14px 6px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: 10,
                  borderBottom: '0.5px solid var(--sep)',
                }}
              >
                Avoid Options
              </div>

              {/* Avoid Tolls */}
              <div
                className="cell"
                style={{
                  cursor: 'pointer',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() =>
                  onChangeAvoidOptions({
                    ...avoidOptions,
                    avoidTolls: !avoidOptions.avoidTolls,
                  })
                }
              >
                <div>
                  <div className="t-body" style={{ fontSize: 13, fontWeight: 600 }}>
                    Tolls
                  </div>
                  <div className="t-foot c2" style={{ fontSize: 11 }}>
                    Avoid toll roads & bridges
                  </div>
                </div>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    border: avoidOptions.avoidTolls ? 'none' : '1.5px solid var(--label3)',
                    backgroundColor: avoidOptions.avoidTolls ? 'var(--accent)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {avoidOptions.avoidTolls && <Check size={12} color="#FFF" strokeWidth={3} />}
                </div>
              </div>

              {/* Avoid Highways */}
              <div
                className="cell"
                style={{
                  cursor: 'pointer',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                onClick={() =>
                  onChangeAvoidOptions({
                    ...avoidOptions,
                    avoidHighways: !avoidOptions.avoidHighways,
                  })
                }
              >
                <div>
                  <div className="t-body" style={{ fontSize: 13, fontWeight: 600 }}>
                    Highways
                  </div>
                  <div className="t-foot c2" style={{ fontSize: 11 }}>
                    Avoid interstates & freeways
                  </div>
                </div>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    border: avoidOptions.avoidHighways ? 'none' : '1.5px solid var(--label3)',
                    backgroundColor: avoidOptions.avoidHighways ? 'var(--accent)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {avoidOptions.avoidHighways && <Check size={12} color="#FFF" strokeWidth={3} />}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Build Route Button */}
        <button
          className="btn-fill"
          style={{
            height: 32,
            borderRadius: 16,
            padding: '0 14px',
            fontSize: 12,
            fontWeight: 600,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
          disabled={!canBuild || isBuilding}
          onClick={() => {
            dismissSuggestions();
            setShowAvoidMenu(false);
            (document.activeElement as HTMLElement)?.blur();
            handleAutoResolveAndBuild(endpoints);
          }}
        >
          {isBuilding ? 'Building...' : 'Build route'}
        </button>
      </div>

      {/* Progress Bar (at bottom edge of card) */}
      {(isBuilding || weatherLoadingProgress !== null) && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 14,
            right: 14,
            height: 3,
            backgroundColor: 'var(--fill3)',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              backgroundColor: 'var(--accent)',
              width:
                weatherLoadingProgress !== null
                  ? `${Math.round(weatherLoadingProgress * 100)}%`
                  : '40%',
              transition: 'width 0.2s ease-out',
            }}
          />
        </div>
      )}

      {/* Reorder Stops Modal */}
      {showReorderModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1100,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReorderModal(false);
          }}
        >
          <div
            className="sheet-surface"
            style={{
              width: '100%',
              maxWidth: 400,
              borderRadius: 28,
              padding: '20px',
              color: 'var(--label)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="t-title2">Reorder Stops</div>
              <button className="cbtn" onClick={() => setShowReorderModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div className="t-foot c2" style={{ marginBottom: 12 }}>
              Start ({startRow.query || 'A'}) and Destination ({destRow.query || 'End'}) stay fixed.
            </div>

            <div className="cell-group" style={{ marginBottom: 16 }}>
              {vias.map((via, vIdx) => (
                <div
                  key={via.id}
                  className="cell"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span className="t-body">
                    {String.fromCharCode(66 + vIdx)}. {via.query || 'Stop'}
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="cbtn"
                      disabled={vIdx === 0}
                      onClick={() => handleMoveVia(vIdx, 'up')}
                      aria-label="Move stop up"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      className="cbtn"
                      disabled={vIdx === vias.length - 1}
                      onClick={() => handleMoveVia(vIdx, 'down')}
                      aria-label="Move stop down"
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="btn-fill"
              style={{ width: '100%' }}
              onClick={() => {
                setShowReorderModal(false);
                handleAutoResolveAndBuild(endpoints);
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
